import { useState, useEffect, useCallback } from "react";
import { FiX } from "react-icons/fi";
import mammoth from "mammoth";
import RichTextEditor from "../RichTextEditor";
import Loader from "../Loader";
import { showErrorToast, showSuccessToast } from "../Toast";
import ENDPOINTS from "../../api/endpoint";

// Helper function to create a formatted DOCX file without using docx library
const createMinimalDocx = async (htmlContent, fileName) => {
  // Load JSZip from CDN to create the ZIP structure
  return new Promise((resolve, reject) => {
    // Check if JSZip is already loaded
    if (window.JSZip) {
      createDocxWithJSZip(window.JSZip, htmlContent, fileName)
        .then(resolve)
        .catch(reject);
      return;
    }

    // Load JSZip from CDN
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    script.onload = () => {
      if (window.JSZip) {
        createDocxWithJSZip(window.JSZip, htmlContent, fileName)
          .then(resolve)
          .catch(reject);
      } else {
        reject(new Error("JSZip not available after loading from CDN"));
      }
    };
    script.onerror = () => {
      reject(new Error("Failed to load JSZip from CDN"));
    };
    document.head.appendChild(script);
  });
};

const createDocxWithJSZip = async (JSZip, htmlContent, fileName) => {
  const zip = new JSZip();

  // Escape XML special characters
  const escapeXml = (text) => {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  };

  // Parse HTML and convert to DOCX XML format with formatting
  const parseHtmlToDocx = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Helper to build run properties (formatting) from a node
    const buildRunProperties = (node) => {
      const props = [];
      let current = node;

      // Check current node and parent nodes for formatting
      while (current && current !== tempDiv && current !== document) {
        const tag = current.tagName?.toLowerCase();
        if (tag === "strong" || tag === "b") {
          props.push("<w:b/>");
        } else if (tag === "em" || tag === "i") {
          props.push("<w:i/>");
        } else if (tag === "u") {
          props.push('<w:u w:val="single"/>');
        }
        current = current.parentElement;
      }

      return props.length > 0 ? `<w:rPr>${props.join("")}</w:rPr>` : "";
    };

    // Convert text nodes and inline elements to runs
    const convertToRuns = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (text.trim()) {
          const rPr = buildRunProperties(node.parentElement);
          return `<w:r>${rPr}<w:t xml:space="preserve">${escapeXml(
            text
          )}</w:t></w:r>`;
        }
        return "";
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
      }

      const tagName = node.tagName?.toLowerCase();

      // Inline formatting elements - process children
      if (["strong", "b", "em", "i", "u", "span"].includes(tagName)) {
        return Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");
      }

      // Block elements
      if (["p", "div"].includes(tagName)) {
        const runs = Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");

        if (runs) {
          return `<w:p>${runs}</w:p>`;
        }
        return `<w:p><w:r><w:t></w:t></w:r></w:p>`;
      }

      if (tagName === "br") {
        return `<w:br/>`;
      }

      // Headings
      if (tagName === "h1") {
        const runs = Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");
        return `<w:p><w:pPr><w:pStyle w:val="Heading1"/></w:pPr>${runs}</w:p>`;
      }

      if (tagName === "h2") {
        const runs = Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");
        return `<w:p><w:pPr><w:pStyle w:val="Heading2"/></w:pPr>${runs}</w:p>`;
      }

      if (tagName === "h3") {
        const runs = Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");
        return `<w:p><w:pPr><w:pStyle w:val="Heading3"/></w:pPr>${runs}</w:p>`;
      }

      // Lists
      if (tagName === "ul") {
        const listItems = Array.from(node.querySelectorAll("li") || []);
        return listItems
          .map((li) => {
            const runs = Array.from(li.childNodes)
              .map(convertToRuns)
              .filter((x) => x)
              .join("");
            return `<w:p><w:pPr><w:numPr><w:ilvl w:val="0"/><w:numId w:val="1"/></w:numPr></w:pPr>${runs}</w:p>`;
          })
          .join("");
      }

      if (tagName === "ol") {
        const listItems = Array.from(node.querySelectorAll("li") || []);
        return listItems
          .map((li) => {
            const runs = Array.from(li.childNodes)
              .map(convertToRuns)
              .filter((x) => x)
              .join("");
            return `<w:p><w:pPr><w:numPr><w:ilvl w:val="0"/><w:numId w:val="2"/></w:numPr></w:pPr>${runs}</w:p>`;
          })
          .join("");
      }

      if (tagName === "li") {
        return Array.from(node.childNodes)
          .map(convertToRuns)
          .filter((x) => x)
          .join("");
      }

      // Default: process children
      return Array.from(node.childNodes)
        .map(convertToRuns)
        .filter((x) => x)
        .join("");
    };

    // Process all top-level nodes
    const paragraphs = Array.from(tempDiv.childNodes)
      .map((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            return `<w:p><w:r><w:t>${escapeXml(text)}</w:t></w:r></w:p>`;
          }
          return "";
        }

        const tagName = node.tagName?.toLowerCase();
        if (["p", "div", "h1", "h2", "h3", "ul", "ol"].includes(tagName)) {
          return convertToRuns(node);
        }
        // For other nodes, wrap in paragraph
        const runs = convertToRuns(node);
        if (runs) {
          return `<w:p>${runs}</w:p>`;
        }
        return "";
      })
      .filter((x) => x);

    if (paragraphs.length === 0) {
      return `<w:p><w:r><w:t></w:t></w:r></w:p>`;
    }

    return paragraphs.join("");
  };

  const bodyContent = parseHtmlToDocx(htmlContent);

  // Create word/document.xml content
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${bodyContent}
  </w:body>
</w:document>`;

  // Create [Content_Types].xml
  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`;

  // Create _rels/.rels
  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

  // Create word/_rels/document.xml.rels
  const wordRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
</Relationships>`;

  // Add files to ZIP
  zip.file("[Content_Types].xml", contentTypesXml);
  zip.folder("_rels").file(".rels", relsXml);
  zip.folder("word").file("document.xml", documentXml);
  zip.folder("word").folder("_rels").file("document.xml.rels", wordRelsXml);

  // Generate blob
  const blob = await zip.generateAsync({ type: "blob" });
  return new File([blob], fileName, {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

// Helper functions to load libraries from CDN to avoid webpack issues
const loadJsPDFFromCDN = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.jspdf && window.jspdf.jsPDF) {
      resolve(window.jspdf.jsPDF);
      return;
    }
    if (window.jsPDF) {
      resolve(window.jsPDF);
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.onload = () => {
      // CDN UMD version exports as window.jspdf.jsPDF or window.jsPDF
      // Wait a bit for the script to fully initialize
      setTimeout(() => {
        const JsPDF = window.jspdf?.jsPDF || window.jsPDF;
        if (JsPDF && typeof JsPDF === "function") {
          resolve(JsPDF);
        } else {
          // Try accessing it differently - sometimes it's directly on window
          const altJsPDF = window.jspdf || window.jsPDF;
          if (altJsPDF && typeof altJsPDF === "function") {
            resolve(altJsPDF);
          } else {
            reject(
              new Error(
                "jsPDF not found or not a constructor after loading from CDN"
              )
            );
          }
        }
      }, 100);
    };
    script.onerror = () => {
      reject(new Error("Failed to load jsPDF from CDN"));
    };
    document.head.appendChild(script);
  });
};

export default function EditDocumentModal({ isOpen, onClose, file }) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helper function to fetch blob URL using CORS proxy
  // This handles CORS issues when fetching from Azure Blob Storage
  // Uses public CORS proxy services to bypass CORS restrictions
  const fetchWithCorsProxy = async (url) => {
    // Skip direct fetch since Azure Blob Storage URLs typically have CORS restrictions
    // Go directly to using CORS proxy services

    // List of CORS proxy services that support binary data
    // These proxies preserve binary content for DOCX, PDF, etc.
    // Order matters - try most reliable first
    const corsProxies = [
      {
        name: "allorigins",
        url: `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
      },
      {
        name: "corsproxy",
        url: `https://corsproxy.io/?${encodeURIComponent(url)}`,
      },
      {
        name: "codetabs",
        url: `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(
          url
        )}`,
      },
    ];

    // Try each CORS proxy until one works
    let lastError = null;
    for (let i = 0; i < corsProxies.length; i++) {
      const proxy = corsProxies[i];
      try {
        console.log(
          `[CORS Proxy] Attempting ${proxy.name} (${i + 1}/${
            corsProxies.length
          })...`
        );

        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        const response = await fetch(proxy.url, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          signal: controller.signal,
          headers: {
            Accept: "*/*",
          },
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          console.log(`[CORS Proxy] Success with ${proxy.name}`);
          return response;
        } else {
          const errorText = await response.text().catch(() => "");
          lastError = new Error(
            `${proxy.name} returned status ${response.status}: ${response.statusText}`
          );
          console.warn(
            `[CORS Proxy] ${proxy.name} returned status ${response.status}`,
            errorText.substring(0, 200)
          );
        }
      } catch (error) {
        if (error.name === "AbortError") {
          console.warn(`[CORS Proxy] ${proxy.name} timed out`);
          lastError = new Error(`${proxy.name} request timed out`);
        } else {
          console.warn(`[CORS Proxy] ${proxy.name} failed:`, error.message);
          lastError = error;
        }
        continue;
      }
    }

    // If all proxies fail, provide helpful error message
    const errorMessage = lastError
      ? `Failed to fetch document through CORS proxies. Last error: ${lastError.message}`
      : "All CORS proxy attempts failed. Please check your internet connection or contact support.";

    console.error("[CORS Proxy] All proxies failed:", errorMessage);
    throw new Error(errorMessage);
  };

  const loadDocument = useCallback(async () => {
    if (!file?.blob_url) {
      showErrorToast("Document URL not available");
      return;
    }

    setLoading(true);
    setContent("");

    try {
      // Fetch the document from blob_url using CORS proxy
      console.log("Fetching document from blob URL using CORS proxy...");
      const response = await fetchWithCorsProxy(file.blob_url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch document: ${response.status} ${response.statusText}`
        );
      }

      console.log(
        "Successfully fetched document, converting to array buffer..."
      );
      // Get the file as an array buffer
      const arrayBuffer = await response.arrayBuffer();
      console.log(
        "Array buffer received, size:",
        arrayBuffer.byteLength,
        "bytes"
      );

      // Check if blob_url ends with .pdf
      const isPdf = file.blob_url?.toLowerCase().endsWith(".pdf");

      // Check file type
      const fileType =
        file.type?.toLowerCase() || file.name?.split(".").pop()?.toLowerCase();

      if (isPdf || fileType === "pdf") {
        // Use pdf.js from CDN to extract text from PDF
        try {
          // Load pdf.js from CDN
          if (!window.pdfjsLib) {
            // Load the script if not already loaded
            await new Promise((resolve, reject) => {
              const script = document.createElement("script");
              script.src =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }

          // Configure worker
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

          const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer })
            .promise;
          const numPages = pdf.numPages;
          const textChunks = [];

          // Extract text from all pages
          for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
              .map((item) => ("str" in item ? item.str : ""))
              .join(" ");
            textChunks.push(pageText);
          }

          // Combine all pages with line breaks and wrap in paragraphs
          const extractedText = textChunks.join("\n\n");
          setContent(`<p>${extractedText.replace(/\n/g, "<br>")}</p>`);
        } catch (pdfError) {
          console.error("Error extracting PDF text:", pdfError);
          showErrorToast(
            `Failed to extract text from PDF: ${pdfError.message}. The PDF might be image-based or corrupted.`
          );
          onClose();
          return;
        }
      } else if (fileType === "docx" || fileType === "doc") {
        // Use mammoth to extract HTML from .docx
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setContent(result.value);

        if (result.messages.length > 0) {
          console.warn("Mammoth conversion warnings:", result.messages);
        }
      } else if (fileType === "txt") {
        // For text files, just use the text content
        const text = await response.text();
        setContent(`<p>${text.replace(/\n/g, "<br>")}</p>`);
      } else {
        showErrorToast(`File type ${fileType} is not supported for editing`);
        onClose();
        return;
      }
    } catch (error) {
      console.error("Error loading document:", error);
      showErrorToast(`Failed to load document: ${error.message}`);
      onClose();
    } finally {
      setLoading(false);
    }
  }, [file, onClose]);

  useEffect(() => {
    if (isOpen && file?.blob_url) {
      loadDocument();
    } else if (isOpen && !file?.blob_url) {
      showErrorToast("Document URL not available");
      onClose();
    }
  }, [isOpen, file, loadDocument, onClose]);

  const handleSave = async () => {
    if (!file?.id || !file?.name) {
      showErrorToast("File information not available");
      return;
    }

    setSaving(true);

    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const token = user.access_token;
      const companyId = user.company_id;
      const userId = user.user_id || user.id;

      if (!companyId || !token) {
        showErrorToast("Missing authentication details");
        setSaving(false);
        return;
      }

      // Get file extension and name
      const fileType =
        file.type?.toLowerCase() || file.name?.split(".").pop()?.toLowerCase();
      const isPdf = file.blob_url?.toLowerCase().endsWith(".pdf");
      const fileName = file.name;

      // Extract plain text from HTML content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;
      const textContent = tempDiv.textContent || tempDiv.innerText || "";

      let fileBlob;

      if (isPdf || fileType === "pdf") {
        // Convert text to PDF (text-based, not image)
        const JsPDF = await loadJsPDFFromCDN();
        // Ensure we have the constructor function
        const JsPDFConstructor =
          typeof JsPDF === "function"
            ? JsPDF
            : JsPDF.jsPDF || JsPDF.default || JsPDF;
        const pdf = new JsPDFConstructor();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - 2 * margin;
        const lineHeight = 7;
        let yPosition = margin;

        // Split text into lines that fit the page width
        const lines = pdf.splitTextToSize(textContent, maxWidth);

        pdf.setFontSize(12);
        pdf.setFont("helvetica", "normal");

        // Add text line by line
        for (let i = 0; i < lines.length; i++) {
          if (yPosition + lineHeight > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(lines[i], margin, yPosition);
          yPosition += lineHeight;
        }

        // Convert PDF to blob
        const pdfBlob = pdf.output("blob");
        fileBlob = new File([pdfBlob], fileName, { type: "application/pdf" });
      } else if (fileType === "docx" || fileType === "doc") {
        // Console log the HTML content from editor for document files
        console.log("HTML content from editor:", content);
        console.log("Extracted text content:", textContent);

        // Create a formatted DOCX file structure without using docx library
        // DOCX is a ZIP file containing XML files
        // Pass HTML content to preserve formatting (bold, italic, headings, lists, etc.)
        fileBlob = await createMinimalDocx(content, fileName);
      } else if (fileType === "txt") {
        // Convert to plain text
        const textBlob = new Blob([textContent], { type: "text/plain" });
        fileBlob = new File([textBlob], fileName, { type: "text/plain" });
      } else {
        showErrorToast(`File type ${fileType} is not supported for saving`);
        setSaving(false);
        return;
      }

      // Upload the file using UPDATE_DOCUMENT API
      const formData = new FormData();
      formData.append("file", fileBlob);
      if (userId) {
        formData.append("user_id", String(userId));
      }

      // Include chatbot_id as query parameter if present
      const updateUrl = ENDPOINTS.UPDATE_DOCUMENT(
        companyId,
        file.id,
        file.chatbot_id || null
      );

      const res = await fetch(updateUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Id": userId,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Failed to update document");
        return;
      }

      // Show success message
      if (data.fallback_to_full) {
        showSuccessToast(
          `Document updated and fully re-processed (${data.total_chunks} chunks)`
        );
      } else if (data.changed_pages) {
        showSuccessToast(
          `Document updated: ${data.changed_pages.length} pages changed, ${data.updated_chunks} chunks updated`
        );
      } else {
        showSuccessToast("Document updated successfully");
      }

      // Close modal and refresh
      onClose();
    } catch (error) {
      console.error("Error saving document:", error);
      showErrorToast(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-300 dark:border-gray-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Document: {file?.name || "Untitled"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader />
            </div>
          ) : (
            <RichTextEditor
              content={content}
              onChange={setContent}
              readOnly={false}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-300 dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="px-4 py-2 bg-indigo-900 hover:bg-indigo-800 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
